import { Box, Chip, Stack, Typography } from "@mui/material";
import { Daphne } from "@syadem/daphne-js";
import { localizedDate } from "../../../../utils/dayjs";
import { getTranslatedFieldByLocale } from "../../../../utils/translatedFields";
import { useDaphne } from "../../../hooks/useDaphne";
import { useI18n } from "../../../hooks/useI18n";
import { theme } from "../../../layout/Theme";
import { TravelQuestionnaireAnswer } from "./travelTypes";

export function TravelQuestionnaireAnswers({
  answers,
  compact = false,
  muted = false,
}: {
  answers: TravelQuestionnaireAnswer[];
  compact?: boolean;
  muted?: boolean;
}) {
  const daphne = useDaphne();
  const { locale, t } = useI18n();
  const labels = getSelectedTravelQuestionnaireAnswerLabels(answers, daphne, locale);

  if (labels.length === 0) return null;

  if (compact) {
    return (
      <Typography
        variant="caption"
        color="inherit"
        sx={{
          color: muted ? theme.palette.neutral[500] : "text.secondary",
          display: "block",
          mt: 0.5,
        }}
      >
        {labels.join(" · ")}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" color={muted ? "text.disabled" : "text.secondary"} display="block" mb={0.75}>
        {t("travel.questionnaire_answers")}
      </Typography>
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {labels.map((label, index) => (
          <Chip
            key={`${label}-${index}`}
            label={label}
            size="small"
            variant="outlined"
            sx={
              muted
                ? {
                    backgroundColor: theme.palette.neutral[100],
                    borderColor: theme.palette.neutral[300],
                    color: theme.palette.neutral[500],
                  }
                : undefined
            }
          />
        ))}
      </Stack>
    </Box>
  );
}

export function getSelectedTravelQuestionnaireAnswerLabels(
  answers: TravelQuestionnaireAnswer[],
  daphne: Daphne,
  locale: ReturnType<typeof useI18n>["locale"],
): string[] {
  return answers.flatMap(({ id, value }) => {
    if (value === null || value === false || value === "") return [];

    const condition = daphne.repositories.conditions.find(id);
    const label = condition ? getTranslatedFieldByLocale(locale, condition.label) : id;

    if (value === true) return [label];

    const formattedValue = condition?.type === "date" && typeof value === "string" ? localizedDate(value, locale) : value;
    return [`${label} : ${formattedValue}`];
  });
}
